/**
 * The only place time advances.
 *
 * `tick` is a pure function: same state, same inputs, same content, same
 * events, every time. No sockets, no fs, no `Date.now()`. That is what makes
 * the determinism assertion in `determinism.test.ts` an architectural property
 * rather than a discipline.
 *
 * Order within a tick matters and is fixed:
 *
 *   1. movement — so an interaction resolves against where the player actually
 *      ended up and what they ended up facing;
 *   2. interactions — the section 5 table, with same-tick contention resolved
 *      before anything is applied;
 *   3. timers — cook, burn, chop, wash and the plate return, which are the
 *      consequences of what just happened rather than competitors with it.
 *
 *   4. orders — tickets appearing and lapsing, last because a serve resolved in
 *      step 2 must be able to fill a ticket that is about to expire, and
 *      because the seat that expiry frees should be available to the ticket
 *      arriving on the same tick.
 */

import type { GameEvent, PlayerInput } from '@overcooked/shared'
import type { Content } from './content'
import { resolveInteractions } from './interact'
import { resolveMovement } from './movement'
import { advanceOrders } from './orders'
import type { GameState } from './state'
import { cloneState } from './state'
import { advanceTimers } from './timers'

export function tick(state: GameState, inputs: PlayerInput[], content: Content): [GameState, GameEvent[]] {
  const next = cloneState(state)
  const events: GameEvent[] = []

  resolveMovement(next, inputs, events)
  resolveInteractions(next, inputs, events, content)
  advanceTimers(next, events, content)
  advanceOrders(next, events, content)

  next.tick = state.tick + 1
  return [next, events]
}
